import { formatDistance, isEqual, isSameDay } from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'
import { DateTime } from 'luxon'
import { SubTask } from 'shared/components/TaskList/TaskList'
import { pluralize } from 'shared/helpers/utils'
import { OnboardingSubChecklist } from 'shared/models/onboarding'
import { SlimTruck, Truck } from 'shared/models/truck'

import { AssignedUser } from '../models/job'
import { AppLoad, AppLoadStop, Load, LoadStop } from '../models/load'
import { ListUser, User } from '../models/user'
import { toCamelCase } from './utils'

// "San Francisco, CA, USA" -> "San Francisco, CA"
// "Tomahawk, Wisconsin, EE. UU." -> "Tomahawk, Wisconsin"

// only works for USA (and EE. UU. for Spanish-speaking users) which is what we need
export const removeCountryName = (location: string) => {
  if (location.includes('USA')) return location.slice(0, -5)
  if (location.includes('EE. UU.')) return location.slice(0, -9)
  return location
}

const formatCurrencyToIntl = (value: string | number) => {
  if (!Intl?.NumberFormat) {
    return value
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  if (typeof value === 'number') {
    return formatter.format(value)
  }

  return formatter.format(Number(value.replace(/(,|\s)/gi, '')))
}

export const formatCurrency = (
  value: string | number | null,
  { removeDecimals = false }: { removeDecimals?: boolean } = {}
): string | number => {
  if (value == null) return ''
  const formattedValue = formatCurrencyToIntl(value)

  if (!removeDecimals) return formattedValue

  return String(formattedValue).replace('.00', '')
}

export function formatCurrencyToNumberLike(currency?: string) {
  if (!currency) return ''
  return currency?.replace(/,/gi, '')
}

export function formatMonths(months: number) {
  const durationYears = Math.floor(months / 12)
  const durationMonths = months - durationYears * 12
  const durationMonthsLabel = durationMonths
    ? `${durationMonths} month${durationMonths !== 1 ? 's' : ''}`
    : ''

  if (durationYears === 0) return durationMonthsLabel
  return `${durationYears} year${durationYears !== 1 ? 's' : ''} ${durationMonthsLabel}`
}

export const getFormattedDate = (date: string, timezone: string = 'America/Los_Angeles'): any => {
  return DateTime.fromISO(date)
    .setZone(timezone)
    .toLocaleString({ ...DateTime.DATETIME_FULL, hourCycle: 'h23' })
}

export const getHoursFromDurationInHours = (hours: number) => {
  return Math.floor(hours)
}

export const getMinutesFromDurationInHours = (duration: number) => {
  return Math.floor((duration % 1) * 60)
}

export const getFormattedDuration = (duration: number) => {
  const hours = getHoursFromDurationInHours(duration)
  const minutes = getMinutesFromDurationInHours(duration)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

export const getTimeDifference = (timestamp1: string, timestamp2: string) => {
  const date1 = new Date(timestamp1)
  const date2 = new Date(timestamp2)
  const diff = Math.abs(date2.getTime() - date1.getTime())
  const diffInHours = diff / (1000 * 3600)
  return getFormattedDuration(diffInHours)
}

export const getTimeDifferenceInDays = (timestamp1: string, timestamp2: string) => {
  const date1 = new Date(timestamp1)
  const date2 = new Date(timestamp2)
  const diff = Math.abs(date2.getTime() - date1.getTime())
  const diffInHours = diff / (1000 * 3600 * 24)
  return Math.ceil(diffInHours)
}

export function getTimeDifferenceFromNowString(
  dateTime: string,
  useAbbreviatedUnit: boolean = true,
  useAbsoluteValue: boolean = false
) {
  /*Helper method to get the different in time from a given date to now in a human readable format.
  Round up to the largest unit of time (day, hour, minute)
  */
  const dateTimeObj = DateTime.fromISO(dateTime)
  const diffInMinutes = Math.round(dateTimeObj.diffNow(['minutes']).minutes)
  const diffInHours = Math.round(dateTimeObj.diffNow(['hours']).hours)
  const diffInDays = Math.round(dateTimeObj.diffNow(['days']).days)

  const unitMap = {
    day: useAbbreviatedUnit ? 'd' : pluralize(diffInDays, ' day', 's', false),
    hour: useAbbreviatedUnit ? 'h' : pluralize(diffInHours, ' hour', 's', false),
    minute: useAbbreviatedUnit ? 'm' : pluralize(diffInMinutes, ' minute', 's', false),
  }

  if (diffInDays)
    return `${useAbsoluteValue ? Math.abs(diffInDays).toFixed(0) : diffInDays.toFixed(0)}${unitMap.day}`

  if (diffInHours)
    return `${useAbsoluteValue ? Math.abs(diffInHours).toFixed(0) : diffInHours.toFixed(0)}${unitMap.hour}`

  return `${useAbsoluteValue ? Math.abs(diffInMinutes).toFixed(0) : diffInMinutes.toFixed(0)}${
    unitMap.minute
  }`
}

export const getShortRelativeTime = (dateTime: DateTime) => {
  // The native toRelative() method returns a string like "12 minutes ago" or "20 days ago"
  // This custom function returns a string like "12m ago" or "12d ago" for brevity
  const now = DateTime.now()
  const diff = now.diff(dateTime, ['days', 'hours', 'minutes', 'seconds'])

  if (diff.days >= 1) return `${Math.floor(diff.days)}d ago`
  if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`
  if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`
  return `${Math.floor(diff.seconds)}s ago`
}

export const getDateFormattedLong = (date: string, timezone: string) => {
  if (date) {
    const dateObj = utcToZonedTime(date, timezone)
    return format(dateObj, 'E, MMM d, yyyy', { timeZone: timezone })
  }
  return ''
}

export const getFormattedDateRange = (startDate: Date, endDate: Date, timezone: string) => {
  if (startDate && endDate) {
    return `${format(startDate, 'MMM d', { timeZone: timezone })}-${format(endDate, 'MMM d', {
      timeZone: timezone,
    })}`
  }
  return ''
}

export const replaceUnderscore = (input: string) => {
  return input ? input.replace(/_/g, ' ') : ''
}

const MS_PER_MINUTE = 60000

export function timeAgoFromMinutes(minutesAgo: number) {
  const postedDate = new Date(new Date().getTime() - minutesAgo * MS_PER_MINUTE)
  return timeAgo(postedDate)
}

export const timeAgo = (dateTime: string | Date) => {
  return formatDistance(new Date(dateTime), new Date(), { addSuffix: true })
}

export const ctRangeFormatter = (load: Load) => {
  return load?.tripDistanceMi
    ? `$${(load?.estimatedRateMin / load?.tripDistanceMi).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })} - $${(load?.estimatedRateMax / load?.tripDistanceMi).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })} /mi`
    : ''
}

export const ctRangeFormatterApp = (load: AppLoad) => {
  return load?.trip_distance_mi
    ? `$${(load?.estimated_rate_min / load?.trip_distance_mi).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })} - $${(load?.estimated_rate_max / load?.trip_distance_mi).toLocaleString('en-US', {
        maximumFractionDigits: 2,
      })} /mi`
    : ''
}

export const ctBidRangeFormatter = (min: number, max: number) =>
  `$${min.toLocaleString('en-US')} - $${max.toLocaleString('en-US')}`

export const ctEstimateFormatter = (load: Load) => {
  return ctBidRangeFormatter(load!.estimatedRateMin, load!.estimatedRateMax)
}

export const ctEstimateFormatterApp = (load: AppLoad) => {
  return ctBidRangeFormatter(load!.estimated_rate_min, load!.estimated_rate_max)
}

export const loadLocationFormatter = (stop?: LoadStop) => {
  const stopCity = stop?.locationCity.toLowerCase() || ''
  const stopState = stop?.locationState || ''
  const stopAddress1 = stop?.locationAddress1 || ''

  return stopAddress1 ? `${stopAddress1}, ${stopCity}, ${stopState}, ` : `${stopCity}, ${stopState}`
}

export const loadLocationFormatterApp = (stop?: AppLoadStop) => {
  const stopCity = stop?.location_city.toLowerCase() || ''
  const stopState = stop?.location_state || ''
  const stopAddress1 = stop?.location_address1 || ''

  return stopAddress1 ? `${stopAddress1}, ${stopCity}, ${stopState}, ` : `${stopCity}, ${stopState}`
}

export const getStopLocation = (locationCity?: string, locationState?: string) => {
  const city = !!locationCity ? locationCity.toLowerCase() : ''
  const state = !!locationState ? locationState : ''

  if (!!city && !!state) return `${city}, ${state}`
  else if (!!city) return city
  else if (!!state) return state
  else return 'Location not provided'
}

export const getDateTimeWindow = (
  dateStart: string | undefined | null,
  dateEnd: string | undefined | null,
  timezone: string,
  {
    destinationDate,
    alwaysShowTime,
    addBullet,
    addYears,
    addTimezoneName,
    use24hr,
  }:
    | {
        destinationDate?: boolean
        alwaysShowTime?: boolean
        addBullet?: boolean
        addYears?: boolean
        addTimezoneName?: boolean
        use24hr?: boolean
      }
    | undefined = {}
) => {
  const options = {
    timeZone: timezone,
    ...(addTimezoneName ? { timeZoneName: 'short' } : {}),
    ...(use24hr ? { hour12: false } : {}),
  }

  if (!dateStart) {
    return destinationDate ? 'Time to be confirmed' : ''
  }

  const dateStartObj = utcToZonedTime(dateStart, timezone)
  const dateEndObj = utcToZonedTime(dateEnd || dateStart, timezone)

  if (isEqual(dateStartObj, dateEndObj)) {
    return format(
      dateStartObj,
      `MMM d${addYears ? ', yyyy' : ''}${addBullet ? ' • ' : ' '}HH:mm zzz`,
      options
    )
  }

  //time window is in same day
  if (isSameDay(dateStartObj, dateEndObj)) {
    return [
      format(dateStartObj, `MMM d${addYears ? ', yyyy' : ''}${addBullet ? ' • ' : ' '}HH:mm`, options),
      format(dateEndObj, 'HH:mm zzz', options),
    ].join(' — ')
  }

  if (alwaysShowTime) {
    return [
      format(dateStartObj, `MMM d${addYears ? ', yyyy' : ''}${addBullet ? ' • ' : ' '}HH:mm`, options),
      format(dateEndObj, `MMM d${addYears ? ', yyyy' : ''}${addBullet ? ' • ' : ' '}HH:mm zzz`, options),
    ].join(' — ')
  }

  return [
    format(dateStartObj, `MMM d${addYears ? ', yyyy' : ''}${addBullet ? ' • ' : ' '}HH:mm`, options),
    format(dateEndObj, 'MMM d zzz', options),
  ].join(' — ')
}

export const getDateFormattedShort = (
  date: string | undefined,
  timezone: string | undefined,
  commaSeparator?: boolean
) => {
  const dateFormat = commaSeparator ? 'EEE, MMM d' : 'EEE • MMM d'
  if (date && timezone) {
    const dateObj = utcToZonedTime(date, timezone)
    return format(dateObj, dateFormat, { timeZone: timezone })
  }
  return ''
}

//format mobile in +1(424)574 - 48347 format
export const formatMobile = (mobile: string): string => {
  let result = mobile
  if (!result) {
    return ''
  }
  if (!!mobile && mobile.startsWith('+1')) {
    result = mobile.slice(2)
  }
  return `+1 (${result.substr(0, 3)}) ${result.substr(3, 3)} - ${result.substr(6, 4)}`
}

// format mobile to copy in 42457448347 format, without +1, () and empty spaces
export const formatMobileToCopy = (mobile: string): string => {
  if (!mobile) return ''
  // Remove all non-digit characters
  const digitsOnly = mobile.replace(/\D/g, '')
  // If number starts with '1', remove it
  return digitsOnly.startsWith('1') && digitsOnly.length > 10 ? digitsOnly.slice(-10) : digitsOnly
}

//get valid mobile value without () and empty spaces
export const convertMobileForAPI = (mobile: string | undefined) => {
  if (!mobile) return ''
  return mobile.replace(/[)( \s_]/g, '')
}

export const setDefaultTimezone = (date: string, timezone: string = 'America/Los_Angeles'): DateTime => {
  return DateTime.fromISO(date).setZone(timezone)
}

// format date to "Feb 19, 2025"
export function formatDate(date: string | Date, { addTimeToDate }: { addTimeToDate?: boolean } = {}) {
  if (typeof date === 'string' && date.length === 10) {
    date = addNoonToDate(date)
  }

  if (addTimeToDate && typeof date === 'string') {
    date = `${date} 12:00:00`
  }

  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// format date to "Feb 19"
export function formatShortDate(
  date: string | Date,
  { addYearIfNotCurrentYear }: { addYearIfNotCurrentYear?: boolean } = {}
) {
  if (typeof date === 'string' && date.length === 10) {
    date = addNoonToDate(date)
  }

  const isSameYear = new Date(date).getFullYear() === new Date().getFullYear()

  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: addYearIfNotCurrentYear && !isSameYear ? '2-digit' : undefined,
  })
}

// format date to "Feb 18 - Feb 19"
export function formatShortDateWindow(dateStart: string, dateEnd: string) {
  if (!dateStart && !dateEnd) return ''

  if (dateStart && !dateEnd) return formatShortDate(dateStart)

  if (!dateStart && dateEnd) return formatShortDate(dateEnd)

  const dateStartObj = new Date(dateStart)
  const dateEndObj = new Date(dateEnd)

  if (isSameDay(dateStartObj, dateEndObj)) {
    return formatShortDate(dateStartObj)
  }

  return `${formatShortDate(dateStartObj)} - ${formatShortDate(dateEndObj)}`
}

// format date to "Feb 18, 2025, 4:00 PM PST"
export function formatDateTime(date: string | Date) {
  if (!date) return ''

  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  })
}

interface TimeFormatOptionsType {
  timezone?: string
  use24hr?: boolean
}

// format time to "4:00 PM"
export function formatTime(date: string | Date, { timezone, use24hr }: TimeFormatOptionsType = {}) {
  if (!date) return ''

  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: !use24hr,
  }

  if (timezone) {
    options.timeZone = timezone
    options.timeZoneName = 'short'
  }

  return new Date(date).toLocaleTimeString('en-US', options)
}

// format time to "4:00 AM - 4:00 PM"
export function formatTimeWindow(
  dateStart: string,
  dateEnd: string,
  timeFormatOptions: TimeFormatOptionsType = {}
) {
  const { timezone, use24hr } = timeFormatOptions
  if (!dateStart && !dateEnd) return ''

  if (dateStart && !dateEnd) return formatTime(dateStart, timeFormatOptions)

  if (!dateStart && dateEnd) return formatTime(dateEnd, timeFormatOptions)

  if (dateStart === dateEnd) return formatTime(dateStart, timeFormatOptions)

  const dateStartObj = new Date(dateStart)
  const dateEndObj = new Date(dateEnd)

  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    hour12: !use24hr,
  }

  return `${new Date(dateStartObj).toLocaleTimeString('en-US', options)} - ${new Date(
    dateEndObj
  ).toLocaleTimeString('en-US', {
    ...options,
    ...(timezone ? { timeZone: timezone, timeZoneName: 'short' } : {}),
  })}`
}

// This will make sure dates without times are rendered as same day across US
export function addNoonToDate(date: string) {
  return new Date(`${date}T12:00:00-0800`)
}

export const FRACTION_REGEX = /^(?:\d+ )?\d+\/\d+$|^\d+$/

export const IS_NUMERIC_REGEX = /^-?\d+(\.\d+)?$/

export const isNumeric = (value: any) => IS_NUMERIC_REGEX.test(value)

export const decimalToFraction = (value: any) => {
  // This will convert a decimal to a fraction
  // ie 2.66666667 -> 2 2/3

  if (!value || value === 'NaN') {
    return
  }

  if (!isNumeric(value)) {
    return value
  }

  // Convert decimal to fraction
  const tolerance = 0.00000001
  const num = parseFloat(value)
  const whole = Math.floor(num)
  const decimal = num - whole

  // For decimals less than the tolerance, return the whole number
  if (decimal < tolerance) {
    return whole.toString()
  }

  // Find the closest fraction
  let numerator = 0
  let denominator = 1
  let minDiff = 1

  // Example:
  // decimal = 0.66666667 should be 2/3
  // 0.66 => 33/50
  // 0.666 => 333/500
  // 0.6666 => 2/3
  // decimal = 0.33333333 should be 1/3
  // decimal = 0.5 should be 1/2
  // decimal = 0.25 should be 1/4
  // decimal = 0.75 should be 3/4
  // decimal = 0.125 should be 1/8
  // decimal = 0.875 should be 7/8

  for (let d = 1; d <= 1000; d++) {
    const n = Math.round(decimal * d)
    const diff = Math.abs(decimal - n / d)
    if (diff < minDiff) {
      minDiff = diff
      numerator = n
      denominator = d
    }
    if (diff < tolerance) break
  }

  // Simplify the fraction
  // Declare a constant function named gcd that takes two numbers and returns a number
  const gcd = (a: number, b: number): number =>
    // If b is non-zero (truthy), recursively call gcd with b and the remainder of a divided by b
    // Otherwise, return a as the base case (when b is zero)
    b ? gcd(b, a % b) : a

  // Example: gcd(12, 18) = 6
  // 12 = 2 * 2 * 3
  // 18 = 2 * 3 * 3
  // GCD = 2 * 3 = 6

  // Calculate the greatest common divisor (GCD) of numerator and denominator
  // This is used to simplify the fraction
  const divisor = gcd(numerator, denominator)
  numerator = Math.floor(numerator / divisor)
  denominator = Math.floor(denominator / divisor)

  // Example: gcd(12, 18) = 6
  // 12 = 2 * 2 * 3
  // 18 = 2 * 3 * 3
  // GCD = 2 * 3 = 6
  // 12 / 6 = 2
  // 18 / 6 = 3
  // 2 / 3 = 2/3
  // 12 / 18 => 2/3

  if (whole === 0) {
    return `${numerator}/${denominator}`
  }
  return `${whole} ${numerator}/${denominator}`
}

export const fractionToDecimal = (value: string) => {
  if (!value) return 0

  const fractionMatch = FRACTION_REGEX.exec(value)
  if (fractionMatch === null || fractionMatch.length === 0) return value

  const [numerator, denominator] = value.split('/').map(Number)
  if (isNaN(numerator) || isNaN(denominator)) return value

  return (numerator / denominator).toFixed(8)
}

export const getInitials = (_user?: User | AssignedUser | ListUser) => {
  // return empty string if user unavailable
  if (!_user || !_user.id) return ''
  // TODO: remove toCamelCase when all data is camelCase
  const user = toCamelCase(_user)
  return `${user.firstName[0]}${user.lastName ? user.lastName[0] : ''}`
}

export const getInitialsFromUsername = (userName: string) => {
  const [firstName, lastName] = userName.split(' ')
  return `${firstName[0]}${lastName ? lastName[0] : ''}`
}

export const formattedName = (user: { firstName: string; lastName: string }) => {
  return user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName
}

// format date to "Feb 2025"
// converts date time string to Month day(number)
export const getDateFormatYearMonth = (date: string | Date) => {
  let dateObj = new Date(date)
  const options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' }
  return dateObj.toLocaleString('en-us', options)
}

export function formatNumber(n: string | number) {
  // format number 1000000 to 1,234,567
  if (!n) return ''
  return String(n)
    .replace(/[^\d.]+/g, '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const formatTruckName = (truck?: SlimTruck | Truck) => {
  if (!truck) {
    return 'No truck'
  }

  const hasVehicleData = truck.vehicleMake || truck.vehicleModel || truck.vehicleYear
  return hasVehicleData
    ? `${truck.vehicleMake || ''} ${truck.vehicleModel || ''} ${truck.vehicleYear || ''}`
    : 'No make model'
}

export const getTruckNameFromTask = (task: OnboardingSubChecklist | SubTask) => {
  const truck = task.context!.truck!
  return formatTruckName(truck)
}

export const getUserName = (user: { firstName: string; lastName: string } | undefined) => {
  return user?.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : 'No name'
}

export const getUserNameFromTask = (task: OnboardingSubChecklist | SubTask) => {
  const user = task.context?.user
  return getUserName(user)
}
